import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  getGooglePlacesRegionCode,
  loadGoogleMapsPlacesApi,
  mapGooglePlaceToCustomerAddress,
} from "../../services/googlePlaces/browser";

const EMPTY_OPTIONS = [];

function getOptionLabel(option) {
  if (typeof option === "string") return option;
  return option?.label || "";
}

function normalizeErrorMessage(error) {
  const message = String(error?.message || "").trim();
  return message || "Smart address search is unavailable right now. Manual entry still works.";
}

export default function SmartAddressAutocomplete({
  googlePlacesConfig,
  country,
  onAddressSelected,
}) {
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingSelection, setLoadingSelection] = useState(false);

  const placesLibraryRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const fetchSequenceRef = useRef(0);

  const isEnabled = googlePlacesConfig?.enabled === true && !!googlePlacesConfig?.apiKey;
  const regionCode = useMemo(
    () => getGooglePlacesRegionCode(country || googlePlacesConfig?.country || ""),
    [country, googlePlacesConfig?.country],
  );

  useEffect(() => {
    let cancelled = false;

    if (!isEnabled) {
      setReady(false);
      setLoadError("");
      setOptions(EMPTY_OPTIONS);
      return undefined;
    }

    setLoadError("");
    loadGoogleMapsPlacesApi(googlePlacesConfig.apiKey)
      .then(async (google) => {
        const placesLibrary = await google.maps.importLibrary("places");
        if (cancelled) return;
        placesLibraryRef.current = placesLibrary;
        setReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setReady(false);
        setLoadError(normalizeErrorMessage(error));
      });

    return () => {
      cancelled = true;
    };
  }, [googlePlacesConfig?.apiKey, isEnabled]);

  useEffect(() => {
    if (!ready || !placesLibraryRef.current) return undefined;
    if (!isEnabled) return undefined;

    const query = String(inputValue || "").trim();
    if (query.length < 3) {
      setOptions(EMPTY_OPTIONS);
      setLoadingSuggestions(false);
      return undefined;
    }

    const sequence = fetchSequenceRef.current + 1;
    fetchSequenceRef.current = sequence;
    setLoadingSuggestions(true);

    const timer = setTimeout(async () => {
      try {
        const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLibraryRef.current;
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
          input: query,
          sessionToken: sessionTokenRef.current,
        };

        if (regionCode) {
          request.region = regionCode;
        }

        const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (fetchSequenceRef.current !== sequence) return;

        const nextOptions = (result?.suggestions || [])
          .filter((suggestion) => suggestion?.placePrediction)
          .map((suggestion) => ({
            id:
              suggestion.placePrediction?.placeId ||
              suggestion.placePrediction?.text?.toString?.() ||
              suggestion.placePrediction?.text?.text ||
              Math.random().toString(36).slice(2),
            label:
              suggestion.placePrediction?.text?.toString?.() ||
              suggestion.placePrediction?.text?.text ||
              "",
            suggestion,
          }))
          .filter((option) => option.label);

        setOptions(nextOptions);
      } catch (error) {
        if (fetchSequenceRef.current !== sequence) return;
        setOptions(EMPTY_OPTIONS);
        setLoadError(normalizeErrorMessage(error));
      } finally {
        if (fetchSequenceRef.current === sequence) {
          setLoadingSuggestions(false);
        }
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [inputValue, isEnabled, ready, regionCode]);

  const handleSelect = async (_event, option) => {
    if (!option?.suggestion?.placePrediction) return;

    setLoadingSelection(true);
    setLoadError("");
    try {
      const place = option.suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });

      onAddressSelected?.(mapGooglePlaceToCustomerAddress(place));
      setInputValue(option.label || "");
      setOptions(EMPTY_OPTIONS);
      if (placesLibraryRef.current?.AutocompleteSessionToken) {
        sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
      } else {
        sessionTokenRef.current = null;
      }
    } catch (error) {
      setLoadError(normalizeErrorMessage(error));
    } finally {
      setLoadingSelection(false);
    }
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <Autocomplete
        freeSolo
        blurOnSelect
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={getOptionLabel}
        loading={loadingSuggestions || loadingSelection}
        inputValue={inputValue}
        onInputChange={(_event, nextValue, reason) => {
          if (reason === "reset") return;
          setInputValue(nextValue || "");
          if (loadError) {
            setLoadError("");
          }
        }}
        onChange={handleSelect}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label="Search address"
            placeholder="Start typing an address..."
            helperText="Smart address search powered by Google. Selecting a suggestion fills the fields below, and you can still edit everything manually."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingSuggestions || loadingSelection ? (
                    <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 2 }}
      />
      {loadError ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {loadError} Manual address entry is still available below.
        </Alert>
      ) : null}
    </>
  );
}
